import { get as getCookie, set as setCookie } from 'js-cookie';
import { throttle } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchFromOldApi } from '~/services/Http';
import { redirectToOrigin } from '~/utils/helper';

export const LAST_USER_INTERACTION_TIME_COOKIE_NAME =
  'last-user-interaction-time';
export const CHECK_TIMEOUT_INTERVAL = 60 * 1000;

export interface UseSessionMonitorOutput {
  minutesToExpiration: string;
  continueSession: () => void;
}

export const useSessionMonitor = (): UseSessionMonitorOutput => {
  const checkTimeoutInterval = useRef<number>();
  const [minutesToExpiration, setMinutesToExpiration] = useState('');

  const continueSession = useCallback(() => {
    setCookie(LAST_USER_INTERACTION_TIME_COOKIE_NAME, Date.now().toString());
    setMinutesToExpiration('');
    // API Call that will make sure BE refreshes the session cookie
    fetchFromOldApi('/json/status').catch(() => {
      redirectToOrigin('Session expired');
    });
  }, [setMinutesToExpiration]);

  const checkTimeout = useCallback(() => {
    const lastUserInteractionTime =
      getCookie(LAST_USER_INTERACTION_TIME_COOKIE_NAME) || 0;
    const iIdleMinutes = Math.round(
      (Date.now() - +lastUserInteractionTime) / CHECK_TIMEOUT_INTERVAL,
    );

    // Session expiration
    if (iIdleMinutes >= 29) {
      window.clearInterval(checkTimeoutInterval.current);
      setMinutesToExpiration('');
      window.location.replace('/page-logout');
      // Session will expire in >>> 2 minutes
    } else if (iIdleMinutes === 27) {
      setMinutesToExpiration('2');
      // Session will expire in >>> 5 minutes
    } else if (iIdleMinutes === 24) {
      setMinutesToExpiration('5');
    }
  }, [setMinutesToExpiration]);

  const isDialogOpen = !!minutesToExpiration;
  useEffect(() => {
    if (!isDialogOpen) {
      const events = [
        'load',
        'mousemove',
        'mousedown',
        'click',
        'scroll',
        'keypress',
      ];

      const throttledContinueSession = throttle(
        continueSession,
        CHECK_TIMEOUT_INTERVAL,
      );

      for (let i in events) {
        window.addEventListener(events[i], throttledContinueSession);
      }

      return () => {
        for (let i in events) {
          window.removeEventListener(events[i], throttledContinueSession);
        }
      };
    }
  }, [continueSession, minutesToExpiration, isDialogOpen]);

  useEffect(() => {
    checkTimeoutInterval.current = window.setInterval(
      checkTimeout,
      CHECK_TIMEOUT_INTERVAL,
    );
    return () => {
      window.clearInterval(checkTimeoutInterval.current);
    };
  }, [checkTimeout]);

  return {
    minutesToExpiration,
    continueSession,
  };
};
